import React, { useState, useCallback } from 'react';
import { FolderOpen, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../common/Button';
import { showDirectoryPicker } from '../../services/FileSystemService';

interface LocationPickerProps {
  value: string;
  onChange: (path: string, handle?: FileSystemDirectoryHandle) => void;
  error?: string;
  disabled?: boolean;
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  value,
  onChange,
  error,
  disabled = false,
}) => {
  const [hasPermission, setHasPermission] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleBrowse = useCallback(async () => {
    if (disabled || isLoading) return;

    setIsLoading(true);
    try {
      const handle = await showDirectoryPicker();

      if (handle) {
        // Permission is granted when user selects folder
        setHasPermission(true);

        // Pass both path display name and handle
        onChange(handle.name, handle);
      }
    } catch (err) {
      console.error('Failed to open directory picker:', err);
      setHasPermission(false);
    } finally {
      setIsLoading(false);
    }
  }, [disabled, isLoading, onChange]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1.5">
        Location
      </label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={value}
            readOnly
            placeholder="Select a folder..."
            className={`
              w-full px-3 py-2 pr-10 bg-gray-800 border rounded-lg text-white placeholder-gray-500
              focus:outline-none focus:ring-1 transition-colors font-mono text-sm cursor-pointer
              ${error
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-700 focus:border-blue-500 focus:ring-blue-500'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            onClick={handleBrowse}
          />
          {/* Permission indicator */}
          {value && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {hasPermission ? (
                <Check className="w-4 h-4 text-green-400" title="Permission granted" />
              ) : (
                <AlertCircle className="w-4 h-4 text-yellow-400" title="Permission may be needed" />
              )}
            </div>
          )}
        </div>
        <Button
          variant="secondary"
          onClick={handleBrowse}
          disabled={disabled || isLoading}
          icon={isLoading ? <Loader2 size={16} className="animate-spin" /> : <FolderOpen size={16} />}
        >
          Browse
        </Button>
      </div>
      {error && (
        <p className="mt-1.5 text-sm text-red-400">{error}</p>
      )}
    </div>
  );
};
